import { catmullRomVec3, Easings, interpolateLinearDirectly, interpolateVec3LinearDirectly } from "@scrolly/renderer";
import type { AnimationGroupSerialized } from "@scrolly/post-types";
import { AnimationType } from "@scrolly/post-types";
import { cubicBezier } from "./cubicBezier";

export class Layer3D {
    position: Vector3Tuple | undefined;
    scale: Vector3Tuple | undefined;
    rotation: Vector3Tuple | undefined;
    opacity: number | undefined;
    /**
     * Port 1: Reveal-Kanal 0..1 für `infographic`-Layer. `undefined`, wenn der
     * Scrolly keine progress-Gruppe hat (alle Alt-Scrollys) — Konsumenten
     * behandeln das als "fertig aufgebaut" (1).
     */
    progress: number | undefined;
    /**
     * Port 2: die sechs animierbaren Kanaele des `effect`-Layers.
     *
     * Anders als `progress` fallen sie NICHT auf `initialValue` zurueck: sie
     * sind `undefined`, solange ihr Kanal nicht aktiv animiert, und der
     * Renderer nimmt dann den statischen Wert aus `content.effect.*`. Ein
     * Rueckfall auf `initialValue` wuerde den Content ueberschreiben.
     * (Entspricht `scalarChannel` in Jans Layer3D.ts.)
     */
    effectSize: number | undefined;
    effectSpeed: number | undefined;
    effectWind: number | undefined;
    effectDepth: number | undefined;
    effectCount: number | undefined;
    effectEdgeFalloff: number | undefined;
    /**
     * Port 3: Bild-Masken/Schatten/Blur-Kanaele.
     *
     * `blur` und `mask` fallen auf initialValue zurueck (statischer Basiswert
     * wie opacity/rotation). `shadowBlur`/`shadowOpacity` nutzen scalarChannel
     * (kein Rueckfall — dann greift content.shadow*). Alle vier sind optional:
     * Alt-Scrollys haben die Gruppen nicht, daher der Guard im Builder.
     */
    blur: number | undefined;
    /** Masken-Fenster-Transform [maskOffsetX, maskOffsetY, maskSize]. */
    mask: Vector3Tuple | undefined;
    shadowBlur: number | undefined;
    shadowOpacity: number | undefined;
    shadowOffsetX: number | undefined;
    shadowOffsetY: number | undefined;
    /** Port "3D-Text": animierbare Laufweite fuer text3d/number3d (scalarChannel). */
    letterSpacing: number | undefined;
    /**
     * Port "Anim-Kanaele": animierbare Licht-Einstellungen (scalarChannel/
     * vectorChannel, kein Rueckfall — dann greift der statische content-Wert).
     * Der light-Zweig in renderLayer wendet sie pro Frame an.
     */
    intensity: number | undefined;
    lightDistance: number | undefined;
    lightDecay: number | undefined;
    lightAngle: number | undefined;
    lightPenumbra: number | undefined;
    lightTarget: Vector3Tuple | undefined;
    lightColor: Vector3Tuple | undefined;
    /**
     * Port "Anim-Kanaele": animierbare planare-Spiegel-Einstellungen (Mirrors B).
     * renderLayer schreibt sie pro Frame in mirrorParams, updateMirrors liest sie.
     */
    mirrorStrength: number | undefined;
    mirrorBlur: number | undefined;
    /** Port "Anim-Kanaele": star-Primitive (per-Frame-Geometrie-Rebuild). */
    starOuterRadius: number | undefined;
    starInnerRadius: number | undefined;
    starPoints: number | undefined;
    /** Port "Anim-Kanaele": Material-Displacement-Staerke (Height-Map). */
    displacementScale: number | undefined;
    /** Port "Anim-Kanaele": Text-Zeilenhoehe. */
    lineHeight: number | undefined;
    /** Pro-Editor-Karte: Ort als Kommazahl (1,4 = 40 % von Ort 1 zu Ort 2). */
    mapPlace: number | undefined;
}

/**
 * Skalarer Kanal ohne Rueckfall auf `initialValue` — siehe Layer3D.effectSize.
 */
// Port "HDRI": auch vom environment-hdri-Modul genutzt (environmentRotationAnim).
export function scalarChannel(group: AnimationGroupSerialized | undefined, t: number): number | undefined {
    if (group && group.active && group.keyframes.length > 0) {
        return interpolateAnimations(group, t) as number;
    }
    return undefined;
}

/**
 * Wie scalarChannel, aber fuer einen Vector3-Kanal (z.B. Licht-Zielpunkt oder
 * -Farbe): der interpolierte [x,y,z] solange aktiv, sonst undefined -> der
 * Aufrufer nimmt den statischen content-Wert.
 */
export function vectorChannel(group: AnimationGroupSerialized | undefined, t: number): Vector3Tuple | undefined {
    if (group && group.active && group.keyframes.length > 0) {
        return interpolateAnimations(group, t) as Vector3Tuple;
    }
    return undefined;
}

function interpolateAnimations(
    animationsGroup: AnimationGroupSerialized,
    t: number
): Vector3Tuple | undefined | unknown {
    const { type, keyframes } = animationsGroup;
    const firstKeyframe = keyframes[0];
    const lastKeyframe = keyframes[keyframes.length - 1];

    // happy path
    if (firstKeyframe.start >= t) {
        return firstKeyframe.value;
    }
    if (lastKeyframe.start <= t) {
        return lastKeyframe.value;
    }

    // could do a bisect here or create a hash table
    const toKeyframeIndex = keyframes.findIndex((k) => k.start >= t);

    const toKey = keyframes[toKeyframeIndex];

    const fromKey = keyframes[toKeyframeIndex - 1];

    // inline linear interpolation
    const minValue = fromKey.start;
    const maxValue = toKey.start;

    const minScale = 0;
    const maxScale = 1;

    const ratio = (maxScale - minScale) / (maxValue - minValue);
    // „custom“ = eigene Kurve aus dem Keyframe-Menü (bezierHandles) — fehlte
    // hier, Easings["custom"] war undefined (10.10.2026). Unbekanntes → linear.
    const handles = (fromKey as { bezierHandles?: number[] }).bezierHandles;
    const easingFunction =
        (fromKey.easing as string) === "custom" && Array.isArray(handles) && handles.length === 4
            ? cubicBezier(handles[0], handles[1], handles[2], handles[3])
            : (Easings as Record<string, (n: number) => number>)[fromKey.easing] ?? Easings.linear;
    const timeInterpolated = Math.min(Math.max(minScale, minScale + ratio * (t - minValue)), maxScale);
    const timeEased = easingFunction(timeInterpolated);

    switch (type) {
        case AnimationType.opacity:
        // Port 1: progress ist wie opacity ein Skalar (0..1).
        case AnimationType.progress:
        // Port 2: die Effekt-Kanaele sind ebenfalls Skalare.
        case AnimationType.effectSize:
        case AnimationType.effectSpeed:
        case AnimationType.effectWind:
        case AnimationType.effectDepth:
        case AnimationType.effectCount:
        case AnimationType.effectEdgeFalloff:
        // Port 3: blur/shadowBlur/shadowOpacity/shadowOffset* sind Skalare.
        case AnimationType.blur:
        case AnimationType.shadowBlur:
        case AnimationType.shadowOpacity:
        case AnimationType.shadowOffsetX:
        case AnimationType.shadowOffsetY:
        // Port "3D-Text": letterSpacing ist ein Skalar.
        case AnimationType.letterSpacing:
        // Port "Anim-Kanaele": Licht- + Spiegel-Skalare.
        case AnimationType.intensity:
        case AnimationType.lightDistance:
        case AnimationType.lightDecay:
        case AnimationType.lightAngle:
        case AnimationType.lightPenumbra:
        case AnimationType.mirrorStrength:
        case AnimationType.mirrorBlur:
        // Port "Anim-Kanaele": star-/Material-/Text-Skalare.
        case AnimationType.starOuterRadius:
        case AnimationType.starInnerRadius:
        case AnimationType.starPoints:
        case AnimationType.mapPlace:
        case AnimationType.displacementScale:
        // Port "HDRI": Umgebungs-Drehung ist ein Skalar-Kanal an den
        // Praesentations-Optionen — fehlte hier, dadurch interpolierten die
        // Keyframes nie (Dome drehte sich nicht mit dem Scrollen, Jans Fund).
        case AnimationType.environmentRotation:
        case AnimationType.lineHeight: {
            return interpolateLinearDirectly(fromKey.value as number, toKey.value as number, timeEased);
        }
        case AnimationType.position: {
            // Glatter Pfad (Pro-Editor-Checkbox): Catmull-Rom-Spline durch die
            // Positions-Keyframes. Fehlte im geteilten Renderer komplett — die
            // Checkbox war seit dem Umstieg der Buehne auf den geteilten Player
            // wirkungslos (Jans Fund 25.08.2026). Logik 1:1 aus dem Original
            // (storyforger Layer3D): Nachbarn dazu, an den Enden geklemmt.
            if (animationsGroup.smoothPath) {
                const beforeFrom = keyframes[toKeyframeIndex - 2] ?? fromKey;
                const afterTo = keyframes[toKeyframeIndex + 1] ?? toKey;
                return catmullRomVec3(
                    beforeFrom.value as Vector3Tuple,
                    fromKey.value as Vector3Tuple,
                    toKey.value as Vector3Tuple,
                    afterTo.value as Vector3Tuple,
                    timeEased
                );
            }
            return interpolateVec3LinearDirectly(
                fromKey.value as Vector3Tuple,
                toKey.value as Vector3Tuple,
                timeEased
            );
        }
        case AnimationType.rotation:
        case AnimationType.scale:
        // Port 3: mask = [offsetX, offsetY, size], komponentenweise wie ein Vektor.
        case AnimationType.mask:
        // Port "Anim-Kanaele": Licht-Vektoren (Zielpunkt, Farbe [r,g,b]).
        case AnimationType.lightTarget:
        case AnimationType.lightColor: {
            return interpolateVec3LinearDirectly(
                fromKey.value as Vector3Tuple,
                toKey.value as Vector3Tuple,
                timeEased
            );
        }
        default:
            return undefined;
    }
}

export function layer3D(animationGroups: TODOAny, t: number): Layer3D {
    const newLayer: Layer3D = {
        opacity:
            animationGroups.opacity.active && animationGroups.opacity.keyframes.length > 0
                ? interpolateAnimations(animationGroups.opacity, t)
                : animationGroups.opacity.initialValue,
        position:
            animationGroups.position.active && animationGroups.position.keyframes.length > 0
                ? interpolateAnimations(animationGroups.position, t)
                : animationGroups.position.initialValue,
        scale:
            animationGroups.scale.active && animationGroups.scale.keyframes.length > 0
                ? interpolateAnimations(animationGroups.scale, t)
                : animationGroups.scale.initialValue,
        rotation:
            animationGroups.rotation.active && animationGroups.rotation.keyframes.length > 0
                ? interpolateAnimations(animationGroups.rotation, t)
                : animationGroups.rotation.initialValue,
        // Port 1: optionaler Kanal — Alt-Scrollys haben keine progress-Gruppe.
        // OHNE diesen Guard wirft der Zugriff auf `.active` bei jedem der 165
        // importierten Scrollys.
        //
        // Ohne aktive Animation UNDEFINED liefern (Jans 1.0: scalarChannel).
        // Der Rueckfall auf initialValue (meist 0) hiess fuer jeden Abnehmer
        // "Fortschritt = 0": Balkendiagramme blieben leer statt fertig
        // gezeichnet (Jans Fund), und bei Schach/Fortschrittsbalken/3D-Zaehler
        // griff der `?? lifeProgress`-Rueckfall nie — sie standen still, statt
        // der Lebenszeit des Layers zu folgen. Alle Abnehmer sind auf
        // `undefined = nimm meinen Standard` ausgelegt.
        progress: scalarChannel(animationGroups.progress, t),
        // Port 2 — bewusst ohne initialValue-Rueckfall, s. Layer3D.effectSize.
        effectSize: scalarChannel(animationGroups.effectSize, t),
        effectSpeed: scalarChannel(animationGroups.effectSpeed, t),
        effectWind: scalarChannel(animationGroups.effectWind, t),
        effectDepth: scalarChannel(animationGroups.effectDepth, t),
        effectCount: scalarChannel(animationGroups.effectCount, t),
        effectEdgeFalloff: scalarChannel(animationGroups.effectEdgeFalloff, t),
        // Port 3 — blur/mask mit initialValue-Rueckfall (statischer Basiswert),
        // aber optional geguardet wie progress (Alt-Scrollys haben die Gruppe
        // nicht -> Zugriff auf .active wuerde werfen).
        blur: animationGroups.blur
            ? animationGroups.blur.active && animationGroups.blur.keyframes.length > 0
                ? (interpolateAnimations(animationGroups.blur, t) as number)
                : (animationGroups.blur.initialValue as number)
            : undefined,
        mask: animationGroups.mask
            ? animationGroups.mask.active && animationGroups.mask.keyframes.length > 0
                ? (interpolateAnimations(animationGroups.mask, t) as Vector3Tuple)
                : (animationGroups.mask.initialValue as Vector3Tuple)
            : undefined,
        // shadowBlur/shadowOpacity/shadowOffset* ohne Rueckfall — dann greift
        // content.shadow*.
        shadowBlur: scalarChannel(animationGroups.shadowBlur, t),
        shadowOpacity: scalarChannel(animationGroups.shadowOpacity, t),
        shadowOffsetX: scalarChannel(animationGroups.shadowOffsetX, t),
        shadowOffsetY: scalarChannel(animationGroups.shadowOffsetY, t),
        // Port "3D-Text": ohne Rueckfall — dann greift primitiveParams.letterSpacing.
        letterSpacing: scalarChannel(animationGroups.letterSpacing, t),
        // Port "Anim-Kanaele": Licht- + Spiegel-Kanaele, ohne Rueckfall — der
        // Renderer nimmt sonst den statischen content-Wert.
        intensity: scalarChannel(animationGroups.intensity, t),
        lightDistance: scalarChannel(animationGroups.lightDistance, t),
        lightDecay: scalarChannel(animationGroups.lightDecay, t),
        lightAngle: scalarChannel(animationGroups.lightAngle, t),
        lightPenumbra: scalarChannel(animationGroups.lightPenumbra, t),
        lightTarget: vectorChannel(animationGroups.lightTarget, t),
        lightColor: vectorChannel(animationGroups.lightColor, t),
        mirrorStrength: scalarChannel(animationGroups.mirrorStrength, t),
        mirrorBlur: scalarChannel(animationGroups.mirrorBlur, t),
        starOuterRadius: scalarChannel(animationGroups.starOuterRadius, t),
        starInnerRadius: scalarChannel(animationGroups.starInnerRadius, t),
        starPoints: scalarChannel(animationGroups.starPoints, t),
        mapPlace: scalarChannel(animationGroups.mapPlace, t),
        displacementScale: scalarChannel(animationGroups.displacementScale, t),
        lineHeight: scalarChannel(animationGroups.lineHeight, t),
    };
    return newLayer;
}
